import { useNavigate } from "react-router-dom";
import { useRef, useEffect, useCallback } from "react";
import { renderLotus, easeInOut } from "../utils/lotusBreathe";
import "./Landing.css";

export const Landing = () => {
  const navigate = useNavigate();
  const lotusRef = useRef(null);
  const animRef = useRef(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const animateLotus = useCallback(() => {
    const canvas = lotusRef.current;
    if (!canvas) return;
    if (canvas.width !== 140) {
      canvas.width = 140;
      canvas.height = 140;
    }
    const CYCLE = 8000; // 4s inhale + 4s exhale
    const t = (Date.now() % CYCLE) / CYCLE;
    const openness = t < 0.5 ? easeInOut(t * 2) : easeInOut(1 - (t - 0.5) * 2);
    renderLotus(canvas, openness, 0, null, "#4A90D9", 0.4 + openness * 0.5);
    animRef.current = requestAnimationFrame(animateLotus);
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(animateLotus);
    return () => cancelAnimationFrame(animRef.current);
  }, [animateLotus]);

  return (
    <div className="landing-root">
      {/* NAV */}
      <nav className="landing-nav" data-testid="landing-nav">
        <button className="landing-logo" onClick={() => scrollTo("hero")}>
          <img src="/images/clarityos-logo.png" alt="ClarityOS" className="landing-logo-img" />
        </button>
        <div className="nav-links">
          <button onClick={() => scrollTo("protocols")}>The Protocol</button>
          <button onClick={() => scrollTo("science")}>The Framework</button>
          <button onClick={() => scrollTo("book")}>The Book</button>
        </div>
        <a
          href="/teams"
          className="nav-teams-pill"
          data-testid="nav-teams-link"
          onClick={(e) => { e.preventDefault(); navigate('/teams'); }}
        >
          Teams →
        </a>
        <button
          className="btn-signin"
          data-testid="landing-signin-btn"
          onClick={() => navigate("/login")}
        >
          Sign In
        </button>
      </nav>

      {/* HERO */}
      <div className="landing-hero" id="hero" data-testid="landing-hero">
        <div className="hero-left">
          <div className="eyebrow">Biology Before Psychology</div>
          <h1>
            What you always wanted
            <br />
            for your <em>brain.</em>
          </h1>
          <p className="hero-sub">
            ClarityOS is 3,000 years of contemplative practice validated with
            evidence from neuroscience. Three protocols that prepare you to lead
            the world of flux.
          </p>

          <div className="hero-protocols">
            {[
              {
                color: "var(--blue)",
                name: "Neuroplasticity Reset",
                desc: "15 minutes daily for a younger brain that can shift states at will",
              },
              {
                color: "var(--amber)",
                name: "High Stakes Ready",
                desc: "3 minutes before high-stakes meetings or decisions",
              },
              {
                color: "var(--purple)",
                name: "Sleep Descent",
                desc: "5 minutes before sleep for deep rest",
              },
            ].map((p) => (
              <div className="hero-proto-item" key={p.name}>
                <span
                  className="hero-proto-dot"
                  style={{ background: p.color }}
                />
                <div>
                  <span className="hero-proto-name">{p.name}</span>
                  <span className="hero-proto-desc">{p.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="hero-proof">
            <span className="proof-item">
              &#9678; Used by senior executives across the Global South
            </span>
            <span className="proof-item">
              &#9678; Most practitioners notice a shift within two weeks
            </span>
          </div>

          <div className="hero-actions">
            <button
              className="btn-primary"
              data-testid="begin-protocol-btn"
              onClick={() => navigate("/login")}
            >
              Begin Your Protocol
            </button>
            <button
              className="btn-ghost"
              data-testid="request-access-btn"
              onClick={() => navigate("/request-access")}
            >
              Request Access
            </button>
          </div>
        </div>

        {/* PHONE MOCKUP */}
        <div className="hero-right">
          <div className="phone-wrap">
            <div className="phone-glow" />
            <div className="phone-frame">
              <div className="phone-notch" />
              <div className="phone-status">
                <span>9:41</span>
                <span>&#9679;&#9679;&#9679;&#9679; WiFi</span>
              </div>
              <div className="phone-label">Morning Protocol</div>
              <div className="phone-tab-row">
                <div className="phone-tab active">Reset</div>
                <div className="phone-tab">Lock</div>
                <div className="phone-tab">Install</div>
              </div>
              <div className="phone-ring-wrap">
                <canvas
                  ref={lotusRef}
                  style={{ width: 140, height: 140 }}
                />
                <div className="phone-time-abs">3:42</div>
              </div>
              <div className="phone-step">
                <div className="phone-step-name">Alternate Nostril</div>
                <div className="phone-step-sub">
                  Nadi Shodhana &nbsp;&middot;&nbsp; ANS Balance
                </div>
              </div>
              <span className="phone-breathe">
                Inhale 4 &nbsp;&middot;&nbsp; Exhale 4 &nbsp;&middot;&nbsp;
                Alternate
              </span>
              <div className="phone-divider" />
              <div className="phone-streak-row">
                {[
                  { v: "14", l: "Streak" },
                  { v: "\u00d71.4", l: "Bonus" },
                  { v: "688", l: "Lifetime NP" },
                ].map((s) => (
                  <div className="phone-streak-item" key={s.l}>
                    <div className="phone-streak-val">{s.v}</div>
                    <div className="phone-streak-lbl">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="phone-bottom-nav">
                <div className="pnav">
                  <div className="pnav-icon">&#9711;</div>
                  <span>States</span>
                </div>
                <div className="pnav active">
                  <div className="pnav-icon">&#9654;</div>
                  <span>Protocol</span>
                </div>
                <div className="pnav">
                  <div className="pnav-icon">&#8963;</div>
                  <span>Progress</span>
                </div>
                <div className="pnav">
                  <div className="pnav-icon">&#9678;</div>
                  <span>Protect</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WHAT MAKES THIS DIFFERENT */}
      <section className="wmtd-section" data-testid="wmtd-section">
        <div className="sec-label">What Makes This Different</div>
        <h2 className="wmtd-headline">
          Fifteen minutes. Measurable. Irreversible.
        </h2>
        <p className="wmtd-intro">
          Most leadership development works on your thinking. This works on your
          biology.
          <br />
          The ClarityOS protocol is a sequenced six-step practice drawn from
          Vedic neuroscience — each step producing a specific, documented
          neurochemical effect. In order. The order is the protocol.
        </p>

        <div className="wmtd-cols">
          <div className="wmtd-col">
            <div className="wmtd-phase-label" style={{ color: "#4A90D9" }}>
              RESET &nbsp;&middot;&nbsp; 5 MIN
            </div>
            <h3 className="wmtd-col-head">
              Your nervous system is no longer in yesterday.
            </h3>
            <p className="wmtd-body">
              Kapalbhati fires a norepinephrine spike — the same alerting
              response as cold exposure, without the cold. Nadi Shodhana
              balances your autonomic nervous system through bilateral nasal
              input to the brainstem. Rechaka locks the parasympathetic state
              via the Hering-Breuer reflex.
            </p>
          </div>
          <div className="wmtd-col">
            <div className="wmtd-phase-label" style={{ color: "#C9A84C" }}>
              LOCK &nbsp;&middot;&nbsp; 3 MIN
            </div>
            <h3 className="wmtd-col-head">
              The gap between stimulus and response. On demand.
            </h3>
            <p className="wmtd-body">
              Trataka — sustained candle gazing — produces the acetylcholine
              surge that opens the neuroplastic window. This is what every
              high-performance coach talks about but rarely knows how to produce
              reliably. Now you can produce it in three minutes, before a board
              meeting.
            </p>
          </div>
          <div className="wmtd-col">
            <div className="wmtd-phase-label" style={{ color: "#4CAF7A" }}>
              INSTALL &nbsp;&middot;&nbsp; 7 MIN
            </div>
            <h3 className="wmtd-col-head">
              Not visualisation. Synaptic consolidation.
            </h3>
            <p className="wmtd-body">
              With the window open, a voice-guided Yoga Nidra body scan installs
              a chosen leadership state — Ground, Observe, Connect, Execute, or
              Integrate — into the substrate. BDNF-mediated. The state you
              choose becomes marginally more accessible every time you practise.
            </p>
          </div>
        </div>

        <div className="wmtd-mic-block">
          <p className="wmtd-mic-lead">The phone listens to your breath.</p>
          <p className="wmtd-mic-body">
            During every pranayama step, the microphone measures your breath — 30
            readings per second. The lotus on screen responds to the actual
            amplitude of your exhale, not a timer. When your breathing is strong,
            it brightens. When you hold, it stills. The visualisation is a live
            readout of your nervous system's state — and during Nadi Shodhana,
            the left and right petals open and close with the exact nostril you
            are using.
          </p>
          <p className="wmtd-mic-footer">
            No wearable. No sensor. Just the phone in your pocket and the breath
            in your body.
          </p>
        </div>

        <div className="wmtd-closing">
          <p className="wmtd-quote">
            "The practice is between you and your nervous system. ClarityOS is
            the protocol."
          </p>
        </div>
      </section>

      <hr className="landing-hr" />

      {/* PROTOCOLS */}
      <section className="landing-section" id="protocols" data-testid="protocols-section">
        <div className="sec-label">The Protocols</div>
        <h2 className="sec-h2">Three protocols. Every moment in your day.</h2>
        <p className="sec-p">
          Each protocol is designed for a specific neurological purpose. You
          choose the one that matches where you are: morning, high stakes, or
          descent into sleep.
        </p>

        <div className="protocols-grid">
          {/* Morning Protocol */}
          <div className="proto-card featured" onClick={() => navigate("/login")}>
            <div className="proto-card-left">
              <div className="proto-badge" style={{ color: "var(--blue)" }}>
                Morning Protocol &nbsp;&middot;&nbsp; Recommended
              </div>
              <div className="proto-title">
                Train your brain's operating state.
              </div>
              <div className="proto-desc">
                A three-phase sequence that resets your autonomic nervous system,
                opens a neuroplastic window, and installs a chosen leadership
                state. Most practitioners notice a shift within two weeks.
              </div>
              <div className="proto-steps">
                <span
                  className="step-tag"
                  style={{
                    color: "var(--blue)",
                    borderColor: "rgba(74,144,217,0.3)",
                    background: "rgba(74,144,217,0.06)",
                  }}
                >
                  RESET
                </span>
                <span
                  className="step-tag"
                  style={{
                    color: "var(--amber)",
                    borderColor: "rgba(200,146,42,0.3)",
                    background: "rgba(200,146,42,0.06)",
                  }}
                >
                  LOCK
                </span>
                <span
                  className="step-tag"
                  style={{
                    color: "var(--teal)",
                    borderColor: "rgba(59,170,122,0.3)",
                    background: "rgba(59,170,122,0.06)",
                  }}
                >
                  INSTALL
                </span>
              </div>
              <div className="proto-timing">
                Morning &nbsp;&middot;&nbsp; After waking
              </div>
            </div>
            <div className="proto-card-right">
              <div className="proto-duration">15 min</div>
              <div className="proto-begin">Begin &#8250;</div>
            </div>
          </div>

          {/* High Stakes Ready */}
          <div className="proto-card" onClick={() => navigate("/login")}>
            <div className="proto-card-left">
              <div className="proto-badge" style={{ color: "var(--amber)" }}>
                High Stakes Ready
              </div>
              <div className="proto-title">
                Reset before what matters most.
              </div>
              <div className="proto-desc">
                Four movements (physiological, visual, identity, and centring),
                voice-guided for eyes-closed use. Designed for a bathroom,
                stairwell, or any 3 minutes before a high-pressure meeting or
                decision.
              </div>
              <div className="proto-steps">
                {["THE", "PHYSIOLOGICAL", "VISUAL", "IDENTITY"].map((tag) => (
                  <span
                    className="step-tag"
                    key={tag}
                    style={{
                      color: "var(--amber)",
                      borderColor: "rgba(200,146,42,0.3)",
                      background: "rgba(200,146,42,0.06)",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className="proto-timing">
                Before meetings &nbsp;&middot;&nbsp; High-pressure moments
              </div>
            </div>
            <div className="proto-card-right">
              <div className="proto-duration">3 min</div>
              <div className="proto-begin">Begin &#8250;</div>
            </div>
          </div>

          {/* Sleep Descent */}
          <div className="proto-card" onClick={() => navigate("/login")}>
            <div className="proto-card-left">
              <div className="proto-badge" style={{ color: "var(--purple)" }}>
                Sleep Descent
              </div>
              <div className="proto-title">
                Clear the day and descend into deep sleep.
              </div>
              <div className="proto-desc">
                Cognitive offload via voice journal, followed by a pranayama
                sequence (Offload, Descend, Bridge) that drives parasympathetic
                dominance and sleep onset.
              </div>
              <div className="proto-steps">
                {["OFFLOAD", "DESCEND"].map((tag) => (
                  <span
                    className="step-tag"
                    key={tag}
                    style={{
                      color: "var(--purple)",
                      borderColor: "rgba(139,126,216,0.3)",
                      background: "rgba(139,126,216,0.06)",
                    }}
                  >
                    {tag}
                  </span>
                ))}
                <span
                  className="step-tag"
                  style={{
                    color: "var(--teal)",
                    borderColor: "rgba(59,170,122,0.3)",
                    background: "rgba(59,170,122,0.06)",
                  }}
                >
                  BRIDGE
                </span>
              </div>
              <div className="proto-timing">
                30 min before sleep &nbsp;&middot;&nbsp; Optional
              </div>
            </div>
            <div className="proto-card-right">
              <div className="proto-duration">5 min</div>
              <div className="proto-begin">Begin &#8250;</div>
            </div>
          </div>
        </div>
      </section>

      <hr className="landing-hr" />

      {/* FIVE STATES */}
      <section className="landing-section" id="science" data-testid="states-section">
        <div className="sec-label">The Science</div>
        <h2 className="sec-h2">Five states, not five skills.</h2>
        <p className="sec-p">
          Skills collapse under pressure. ClarityOS trains the substrate beneath
          them: five leadership states that make every skill more effective when
          the stakes are highest.
        </p>
        <div className="states-row">
          {[
            {
              name: "Ground",
              desc: "Who you are when pressure strips away the performance",
              color: "#3baa7a",
            },
            {
              name: "Observe",
              desc: "Seeing clearly without being captured by what you see",
              color: "#4A90D9",
            },
            {
              name: "Connect",
              desc: "Genuine presence. Curiosity over agenda",
              color: "#C47FB5",
            },
            {
              name: "Execute",
              desc: "Decisive movement from commitment, not fear",
              color: "#E8923A",
            },
            {
              name: "Integrate",
              desc: "Synthesising complexity into wisdom, not just conclusions",
              color: "#5db87a",
            },
          ].map((s) => (
            <div className="state-card" key={s.name}>
              <div className="state-dot" style={{ background: s.color }} />
              <div className="state-name">{s.name}</div>
              <div className="state-desc">{s.desc}</div>
            </div>
          ))}
        </div>
        <p className="states-footer-text">
          ClarityOS trains you to access all five, not just the ones you already
          default to.
        </p>
      </section>

      <hr className="landing-hr" />

      {/* PROGRESS */}
      <section className="landing-section" data-testid="progress-section">
        <div className="sec-label">The Progress Layer</div>
        <h2 className="sec-h2">
          Practice leaves a record.
          <br />
          <em>The record changes the practice.</em>
        </h2>
        <p className="sec-p">
          Your Progress dashboard tracks lifetime sessions, consistency, streak
          bonuses, weekly completion, and biofeedback, so the protocol becomes a
          longitudinal evidence base, not just a daily habit.
        </p>

        <div className="dash-grid">
          {[
            {
              label: "Lifetime NP",
              value: "688",
              sub: "Neuroplasticity points accrued",
            },
            {
              label: "Consistency",
              value: "13%",
              sub: "30-day rolling average",
            },
            {
              label: "Current Streak",
              value: "0",
              sub: "Days consecutive \u00a0\u00b7\u00a0 streak bonus \u00d71.0",
            },
            {
              label: "Total Days",
              value: "4",
              sub: "Completed sessions",
            },
          ].map((d) => (
            <div className="dash-card" key={d.label}>
              <div className="dash-label">{d.label}</div>
              <div className="dash-value">{d.value}</div>
              <div className="dash-sub">{d.sub}</div>
            </div>
          ))}
          <div className="dash-card dash-wide">
            <div className="dash-label">This Week</div>
            <div className="week-row">
              {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map(
                (day, i) => (
                  <div
                    className={`day-box${i === 6 ? " today" : ""}`}
                    key={day}
                  >
                    {day}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        <p className="dash-footer-text">
          Challenges (7-Day Fluidity, 30-Day Master), group leaderboards, and
          biofeedback HRV integration are built into the platform, available once
          you're inside.
        </p>
      </section>

      <hr className="landing-hr" />

      {/* SCIENCE PILLARS */}
      <section className="landing-section" data-testid="science-section">
        <div className="sec-label">Why It Works</div>
        <h2 className="sec-h2">Biology before psychology.</h2>
        <p className="sec-p">
          Leadership advice tells you what to do. ClarityOS changes the
          biological conditions under which you do it.
        </p>
        <div className="science-cols">
          {[
            {
              num: "01",
              title: "Norepinephrine reset",
              desc: "Kapalbhati and Nadi Shodhana clear autonomic dysregulation before the day\u2019s decisions accumulate on top of unresolved stress.",
            },
            {
              num: "02",
              title: "Neuroplastic window",
              desc: "Trataka (focused gazing) triggers an acetylcholine surge: the signal your brain uses to enter a state of heightened plasticity and learning readiness.",
            },
            {
              num: "03",
              title: "BDNF consolidation",
              desc: "Yoga Nidra releases BDNF, the protein that consolidates new neural wiring. The leadership state you install during LOCK gets structurally reinforced during INSTALL.",
            },
            {
              num: "04",
              title: "Parasympathetic descent",
              desc: "The Sleep Descent protocol activates the parasympathetic nervous system through pranayama sequencing, clearing cognitive load so sleep architecture improves.",
            },
          ].map((s) => (
            <div className="science-item" key={s.num}>
              <div className="science-num">{s.num}</div>
              <div className="science-title">{s.title}</div>
              <div className="science-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <hr className="landing-hr" />

      {/* BOOK */}
      <div className="book-section" id="book" data-testid="book-section">
        <img
          src="/images/book-cover.png"
          alt="ClarityOS Book Cover"
          className="book-cover-img"
          data-testid="book-cover-img"
        />
        <div>
          <div className="book-available">Coming Soon</div>
          <div className="book-title">
            ClarityOS: What you always wanted for your brain.
          </div>
          <div className="book-author">Debu Mishra</div>
          <div className="book-cred">
            Former Partner/Leader, Deloitte / Hay Group / Arthur Andersen / PwC
            <br />
            Leadership advisor to C-suite executives across the Global South
          </div>
          <div className="book-tagline">
            The book behind the protocols. The complete science and evidence of
            practice.
          </div>
          <div className="book-actions">
            <button
              className="btn-primary"
              data-testid="notify-me-btn"
              onClick={() => navigate("/book")}
            >
              Notify Me
            </button>
            <a
              className="btn-ghost"
              href="https://cal.com/debu-mishra-author"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="discovery-call-btn"
            >
              Book a Discovery Call
            </a>
          </div>
        </div>
      </div>

      {/* TEAMS TEASER */}
      <section className="teams-teaser" data-testid="teams-teaser">
        <div className="teams-teaser-inner">
          <div className="teams-teaser-content">
            <div className="teams-teaser-eyebrow">
              <span className="teams-teaser-line"></span>
              ClarityOS Teams
            </div>
            <h3 className="teams-teaser-heading">
              What does your leadership team look like<br />when no one is watching?
            </h3>
            <p className="teams-teaser-desc">
              Realtime leadership assessment in the flow of high-stakes work.
              Meeting by meeting. Leader by leader.
            </p>
          </div>
          <div className="teams-teaser-cta-wrap">
            <a
              href="/teams"
              className="teams-teaser-cta"
              data-testid="teams-teaser-link"
              onClick={(e) => { e.preventDefault(); navigate('/teams'); }}
            >
              Explore ClarityOS Teams &rarr;
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer" data-testid="landing-footer">
        <div className="footer-left">
          &copy; 2026 ClarityOS &mdash; A product of Shim Partners Management
          Consultancy FZCO, UAE
        </div>
        <div className="footer-right">
          Train the substrate. The performance follows.
        </div>
      </footer>
    </div>
  );
};
